import React, { useState, useRef, useEffect } from 'react'
import { convertStorageUrlToCdn } from 'utils/common'

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string
  alt: string
  className?: string
  loading?: 'lazy' | 'eager'
  enableIntersectionObserver?: boolean
  onError?: () => void
  showErrorPlaceholder?: boolean
}

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className,
  loading = 'lazy',
  enableIntersectionObserver = false,
  onError,
  showErrorPlaceholder = true,
  ...props
}) => {
  const [isInView, setIsInView] = useState(!enableIntersectionObserver)
  const [imageError, setImageError] = useState(false)
  const [useWebp, setUseWebp] = useState(true)
  const imgRef = useRef<HTMLDivElement>(null)

  // WebP URL 생성 함수
  const createWebpUrl = (originalUrl: string): string => {
    return originalUrl.replace(/\.(png|jpg|jpeg)$/i, '.webp')
  }

  // CDN URL 변환
  const webpUrl = convertStorageUrlToCdn(createWebpUrl(src))
  const originalUrl = convertStorageUrlToCdn(src)

  // 이미지 에러 핸들링
  const handleImageError = () => {
    if (useWebp) {
      // WebP에서 실패했다면 원본 시도
      setUseWebp(false)
    } else {
      // 원본도 실패했다면 에러 상태
      setImageError(true)
      onError?.()
    }
  }

  // Intersection Observer 설정
  useEffect(() => {
    if (!enableIntersectionObserver || !imgRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
      }
    )

    observer.observe(imgRef.current)

    return () => observer.disconnect()
  }, [enableIntersectionObserver])

  // 에러 상태 렌더링
  if (imageError && showErrorPlaceholder) {
    return (
      <div ref={imgRef} className={`w-full h-full bg-gray-200 flex items-center justify-center ${className}`}>
        <div className="text-gray-400 text-sm text-center">
          <div>이미지를</div>
          <div>불러올 수 없습니다</div>
        </div>
      </div>
    )
  }

  return (
    <div ref={imgRef} className="w-full h-full">
      {isInView && !imageError && (
        <picture>
          {useWebp && <source srcSet={webpUrl} type="image/webp" />}
          <img
            src={useWebp ? originalUrl : originalUrl}
            alt={alt}
            className={className}
            loading={loading}
            onError={handleImageError}
            {...props}
          />
        </picture>
      )}
    </div>
  )
}